import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { useApp } from "../context/AppContext";
import { PageHeader, TableShell, EmptyRow } from "../components/Shared";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const empty = { name: "", contact_name: "", phone: "", email: "", items_supplied: "", notes: "" };

export default function Suppliers() {
  const { t } = useApp();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const load = () => api.get("/suppliers").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editId) await api.put(`/suppliers/${editId}`, form);
      else await api.post("/suppliers", form);
      toast.success(t("saved")); setOpen(false); load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const remove = async (id) => {
    if (!window.confirm(t("confirmDelete"))) return;
    try { await api.delete(`/suppliers/${id}`); toast.success(t("deleted")); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div>
      <PageHeader title={t("suppliers")}>
        <Button data-testid="add-supplier-button" onClick={() => { setForm(empty); setEditId(null); setOpen(true); }}
          className="h-11 bg-sky-600 hover:bg-sky-700 text-white font-semibold">
          <Plus size={18} className="mr-2" /> {t("addSupplier")}
        </Button>
      </PageHeader>
      <TableShell headers={[t("name"), t("contactName"), t("phone"), t("email"), t("itemsSupplied"), t("actions")]}>
        {rows.length === 0 && <EmptyRow colSpan={6} />}
        {rows.map((r) => (
          <tr key={r.id} className="hover:bg-slate-50">
            <td className="px-4 py-3 font-semibold text-slate-900">{r.name}</td>
            <td className="px-4 py-3">{r.contact_name}</td>
            <td className="px-4 py-3">{r.phone}</td>
            <td className="px-4 py-3">{r.email}</td>
            <td className="px-4 py-3 text-slate-500">{r.items_supplied}</td>
            <td className="px-4 py-3">
              <div className="flex gap-1">
                <Button data-testid={`edit-supplier-${r.id}`} variant="ghost" size="icon"
                  onClick={() => { setForm({ ...empty, ...r }); setEditId(r.id); setOpen(true); }}>
                  <Pencil size={15} />
                </Button>
                <Button data-testid={`delete-supplier-${r.id}`} variant="ghost" size="icon" className="text-red-500 hover:text-red-700" onClick={() => remove(r.id)}>
                  <Trash2 size={15} />
                </Button>
              </div>
            </td>
          </tr>
        ))}
      </TableShell>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">{editId ? t("editSupplier") : t("addSupplier")}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 space-y-1.5">
                <Label>{t("name")} *</Label>
                <Input data-testid="supplier-name-input" required value={form.name} onChange={set("name")} className="h-11" />
              </div>
              <div className="space-y-1.5">
                <Label>{t("contactName")}</Label>
                <Input data-testid="supplier-contact-input" value={form.contact_name} onChange={set("contact_name")} className="h-11" />
              </div>
              <div className="space-y-1.5">
                <Label>{t("phone")}</Label>
                <Input data-testid="supplier-phone-input" value={form.phone} onChange={set("phone")} className="h-11" />
              </div>
              <div className="space-y-1.5">
                <Label>{t("email")}</Label>
                <Input data-testid="supplier-email-input" type="email" value={form.email} onChange={set("email")} className="h-11" />
              </div>
              <div className="space-y-1.5">
                <Label>{t("itemsSupplied")}</Label>
                <Input data-testid="supplier-items-input" value={form.items_supplied} onChange={set("items_supplied")} className="h-11" />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" className="h-11" onClick={() => setOpen(false)}>{t("cancel")}</Button>
              <Button data-testid="supplier-save-button" type="submit" className="h-11 bg-sky-600 hover:bg-sky-700 text-white">{t("save")}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
